import React, { useState } from 'react';
import { StepProps, Specialty, SPECIALTY_COLORS, SPECIALTY_TEXT_COLORS, SPECIALTY_BORDER_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Heart, FlaskConical, Stethoscope, ArrowRight } from 'lucide-react';
export function QuizScreen({
  state,
  updateState,
  nextStep
}: StepProps) {
  const [selected, setSelected] = useState<Specialty | null>(state.quizAnswer);
  const [showFeedback, setShowFeedback] = useState(!!state.quizAnswer);
  const specialties: {
    id: Specialty;
    icon: React.ElementType;
    label: string;
  }[] = [{
    id: 'Nephrologist',
    icon: Droplets,
    label: 'Nephrologist'
  }, {
    id: 'Cardiologist',
    icon: Heart,
    label: 'Cardiologist'
  }, {
    id: 'Endocrinologist',
    icon: FlaskConical,
    label: 'Endocrinologist'
  }, {
    id: 'PCP',
    icon: Stethoscope,
    label: 'PCP'
  }];
  const handleSelect = (id: Specialty) => {
    if (showFeedback) return;
    setSelected(id);
    updateState({
      quizAnswer: id
    });
    setTimeout(() => setShowFeedback(true), 500);
  };
  const getFeedback = () => {
    switch (selected) {
      case 'Nephrologist':
        return 'Surprising insight: Nephrologists often adopt a more cautious, reactive approach due to late-stage referrals and safety concerns.';
      case 'Cardiologist':
        return "You're right — cardiologists tend to be among the most proactive, embracing early multi-modal treatment.";
      case 'Endocrinologist':
        return 'Close — Endocrinologists are proactive but primarily through a metabolic lens, often deferring non-glycaemic treatment.';
      case 'PCP':
        return 'PCPs manage early-stage CKD but tend to adopt conservative approaches and refer for escalation.';
      default:
        return '';
    }
  };
  return <div className="flex-grow flex flex-col justify-center items-center px-6 py-12" data-id="element-371">
      <div className="max-w-3xl w-full mx-auto" data-id="element-372">
        <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" data-id="element-373">
          Which specialty do you think is{' '}
          <span className="text-brand" data-id="element-374">MOST proactive</span> in managing CKD?
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-375">
          {specialties.map(spec => {
          const Icon = spec.icon;
          const isSelected = selected === spec.id;
          const isFaded = showFeedback && !isSelected;
          return <motion.button key={spec.id} whileHover={!showFeedback ? {
            scale: 1.02
          } : {}} whileTap={!showFeedback ? {
            scale: 0.98
          } : {}} onClick={() => handleSelect(spec.id)} className={`
                  relative p-6 rounded-2xl border-2 text-left flex items-center transition-all duration-300
                  ${isSelected ? `border-[${SPECIALTY_COLORS[spec.id]}] bg-gray-50` : 'border-gray-200 hover:border-gray-300 bg-white'}
                  ${isFaded ? 'opacity-50 grayscale' : 'opacity-100'}
                `} style={{
            borderColor: isSelected ? SPECIALTY_COLORS[spec.id] : undefined
          }} data-id="element-376">
                <div className={`p-4 rounded-full mr-4`} style={{
              backgroundColor: `${SPECIALTY_COLORS[spec.id]}15`,
              color: SPECIALTY_COLORS[spec.id]
            }} data-id="element-377">
                  <Icon size={28} data-id="element-378" />
                </div>
                <span className="text-xl font-semibold" data-id="element-379">{spec.label}</span>
              </motion.button>;
        })}
        </div>

        <AnimatePresence data-id="element-380">
          {showFeedback && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mt-8" data-id="element-381">
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 mb-8" data-id="element-382">
                <p className="text-lg text-gray-800 leading-relaxed font-medium" data-id="element-383">
                  {getFeedback()}
                </p>
              </div>
              <div className="flex justify-center" data-id="element-384">
                <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-385">
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-386" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}